import React from 'react';
import {
  BloodworkAnalysis,
  FurtherConsiderations,
  PanelMarker,
  Section,
  RootCauseCategory,
} from '../../models';
import { AnemiaStatus } from './bloodwork-anemia-status';
import { ReferenceRangeAnalysisContainer } from '../../containers/bloodwork-ref-range-analysis-container';
import { WBCEvaluation } from './bloodwork-wbc-evaluation';
import { GlucoseRegulationInsights } from './glucose-regulation-insights';
import { Card } from 'react-bootstrap';

type Props = {
  withNotes?: boolean;
  bloodworkAnalysis: BloodworkAnalysis;
  furtherConsiderations: FurtherConsiderations;
  title: string;
  section: Section;
  panelMarkers: PanelMarker[];
};

export class PDFSection extends React.Component<Props, {}> {
  furtherConsiderationsFor(
    rootCauseCategory: RootCauseCategory,
    section: string,
    tab: string,
  ): JSX.Element {
    const { furtherConsiderations } = this.props;
    return (
      <span key={rootCauseCategory}>
        {
          // tslint:disable-next-line:max-line-length
          furtherConsiderations
            .findConsiderations(rootCauseCategory, section, tab)
            .map((consideration, cindex) => {
              return (
                <span key={cindex}>
                  <span key={`title-${cindex}`} className="consideration-title">
                    {consideration.title}
                  </span>
                  <ul>
                    {consideration.items.map((item, itemIndex) => {
                      return (
                        <li key={`${rootCauseCategory}-${cindex}-${itemIndex}`}>
                          {item}
                        </li>
                      );
                    })}
                  </ul>
                </span>
              );
            })
        }
      </span>
    );
  }

/*
  preparedFor(bloodworkAnalysis: BloodworkAnalysis) {
    return (
      <div>
        <h6 className="prepared-for-container">
          <strong>Prepared for: </strong>
          <span>
            {bloodworkAnalysis.bloodwork.client.firstName}{' '}
            {bloodworkAnalysis.bloodwork.client.lastName}
          </span>
          <strong style={{ marginLeft: 20 }}>Collection Date: </strong>
          <span>
            <Moment format="MM/DD/YYYY">
              {bloodworkAnalysis.bloodwork.collectionDate}
            </Moment>
          </span>
        </h6>
      </div>
    );
  }
*/

  evaluation(
    title: string,
    section: string,
    bloodworkAnalysis: BloodworkAnalysis,
  ) {
    if (section === Section.RBC) {
      return (
        <div className="html2pdf__page-break">
          <h3>{title}</h3>
          {this.furtherConsiderationsFor(
            RootCauseCategory.PDF,
            section,
            'Evaluation',
          )}
          <div className="pdf-anemia-status">
            <AnemiaStatus bloodworkAnalysis={bloodworkAnalysis} />
          </div>
        </div>
      );
    } else if (section === Section.WBC) {
      return (
        <div className="html2pdf__page-break">
          <h3>{title}</h3>
          {this.furtherConsiderationsFor(
            RootCauseCategory.PDF,
            section,
            'Evaluation',
          )}
          <div className="pdf-anemia-status">
            <WBCEvaluation bloodworkAnalysis={bloodworkAnalysis} />
          </div>
        </div>
      );
    } else if (section === Section.GLUCOSE) {
      return (
        <div className="html2pdf__page-break">
          <h3>{title}</h3>
          {this.furtherConsiderationsFor(
            RootCauseCategory.PDF,
            section,
            'Evaluation',
          )}
          <div className="pdf-anemia-status">
            <GlucoseRegulationInsights bloodworkAnalysis={bloodworkAnalysis} />
          </div>
        </div>
      );
    }
    return null;
  }

  refRangeTitle(): string {
    const { section } = this.props;
    if (section === Section.WBC) {
      return 'Immune Results';
    } else if (section === Section.PLATELETS) {
      return 'Blood Clotting Results';
    }
    else if (section === Section.PROTEINS) {
      return 'Protein Results';
    }
    else if (section === Section.NUTRIENTS) {
      return 'Nutrient Results';
    }
    else if (section === Section.SEX_HORMONE) {
      return 'Hormone Results';
    }
    else if (section === Section.INFLAMMATION_OXIDATIVE_STRESS) {
      return 'Inflammation & Oxidative Stress Results';
    }
    else if (section === Section.LIPIDS) {
      return 'Lipid Results';
    }
    else if (section === Section.LIVER_BILIARY) {
      return 'Liver Results';
    }
    else if (section === Section.ELECTROLYTES) {
      return 'Electrolyte Results';
    }
    return `${section} Results`;
  }

  render() {
    const { bloodworkAnalysis, panelMarkers, title, section } = this.props;
    if (panelMarkers.length === 0) {
      return null;
    }
    // Clean the leading blank line(s) from notes
    const noteObj = bloodworkAnalysis.bloodwork.bloodworkNotes.notes.find(
      n => n.section === section
    );
    const notesText = (noteObj?.text ?? '')
      .trimStart(); // removes all leading spaces/newlines


    const infoLink = `https://labsmarts.com/bibliography/#${section}`;
    const pageBreakClassName =
      section === Section.RBC || section === Section.WBC
        ? 'html2pdf__page-break'
        : '';
    return (
      <>
        {this.evaluation(title, section, bloodworkAnalysis)}
        <div className={pageBreakClassName}>
          <h3>{title}</h3>
          {this.furtherConsiderationsFor(
            RootCauseCategory.PDF,
            section,
            'Analysis',
          )}
          <div className={this.props.withNotes && notesText ? 'pdf-section-with-notes' : ''}>
          <div className="pdf-ref-range-analysis">
            <ReferenceRangeAnalysisContainer
              title={this.refRangeTitle()}
              section={section}
              bloodwork={bloodworkAnalysis.bloodwork}
              infoLink={infoLink}
              panelMarkers={panelMarkers}
            />
          </div>
          {this.props.withNotes && notesText && (
            <>
              <Card className="disclaimer-panel mb-5" style={{ whiteSpace: 'pre-line' }}>
                <Card.Header className="py-1">Section Notes</Card.Header>
                <Card.Body
                  className="disclaimer-body"
                  style={{ whiteSpace: 'pre-line', paddingTop: 0, marginTop: 0 }}
                >{notesText}</Card.Body>
              </Card>
              {notesText.split('\n').length >= 33 && <div className="html2pdf__page-break" />}
            </>
          )}
        </div>


        </div>
        {/* <PDFTab
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Insights"
                    section={section}
                    tab="Interpretation"
                />                
                <PDFTab
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Additional Testing"
                    section={section}
                    tab="Testing"
                />
                <PDFTab 
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Further Considerations"
                    section={section}
                    tab="Considerations"
                />
                <PDFTab 
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Signs and Symptoms"
                    section={section}
                    tab="Symptoms"
                />
                <PDFTab 
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Diet"
                    section={section}
                    tab="Diet"
                />
                <PDFTab 
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Lifestyle"
                    section={section}
                    tab="Lifestyle"
                />
                <PDFTab 
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Supplements"
                    section={section}
                    tab="Supplements"
                />
                */}
      </>
    );
  }
}
